/**
 * <s-review-write> — empty-state "Write a review" button on products with no reviews (templates/product.liquid).
 * Judge.me's own widget sits visually hidden inside [data-jdgm-holder] so the app still initialises it.
 * On click: the "#" jump of Judge.me's link is blocked, we wait until Judge.me has set the widget up,
 * then click its own write-review trigger on the next tick (so our click can't close its modal).
 * If no form shows up, the native widget is revealed and scrolled to as a fallback.
 */
const TRIGGERS = '.jdgm-write-rev-link, [class*="write-rev"], [class*="write-review"] button, button[class*="write-review"], button[class*="write"], a[class*="write"]';
const FORM = '.jdgm-form, .jdgm-form-wrapper, [class*="jdgm-modal"], [class*="write-review-modal"], [class*="jdgm-write-rev-modal"]';

const isShown = (el) => el instanceof HTMLElement && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';

class SReviewWrite extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-review-write]')?.addEventListener('click', this.#onClick);
    this.addEventListener('click', this.#blockHashJump, true);
  }

  disconnectedCallback() {
    this.querySelector('[data-review-write]')?.removeEventListener('click', this.#onClick);
    this.removeEventListener('click', this.#blockHashJump, true);
  }

  #blockHashJump = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href="#"]') : null;
    if (link) event.preventDefault();
  };

  #reveal() {
    const holder = this.querySelector('[data-jdgm-holder]');
    if (holder) {
      holder.classList.remove('sr-only');
      holder.removeAttribute('aria-hidden');
      holder.removeAttribute('inert');
    }
    const intro = this.querySelector('[data-review-empty]');
    if (intro) intro.hidden = true;
    return holder;
  }

  #ready() {
    const widget = this.querySelector('.jdgm-review-widget, #judgeme_product_reviews');
    return Boolean(
      this.querySelector('.jdgm--done-setup') ||
        widget?.classList.contains('jdgm--done-setup') ||
        (window.jdgm && typeof window.jdgm === 'object' && window.jdgm._loaderExecuted && window.jdgm.$),
    );
  }

  async #waitReady(limit = 4000) {
    const start = performance.now();
    while (!this.#ready() && performance.now() - start < limit) {
      await new Promise((r) => setTimeout(r, 120));
    }
  }

  #formOpen() {
    return [...document.querySelectorAll(FORM)].some(isShown);
  }

  #onClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget;
    if (button instanceof HTMLButtonElement) button.disabled = true;

    await this.#waitReady();
    const holder = this.#reveal();
    await new Promise((r) => setTimeout(r, 50));

    // Judge.me's current widget renders its own button after it boots; prefer a visible trigger, wait up to 3s for one.
    const findTrigger = () => {
      const all = [...this.querySelectorAll(TRIGGERS)].filter((el) => el instanceof HTMLElement);
      return all.find(isShown) || null;
    };
    let trigger = findTrigger();
    const waitStart = performance.now();
    while (!trigger && performance.now() - waitStart < 3000) {
      await new Promise((r) => setTimeout(r, 150));
      trigger = findTrigger();
    }
    if (trigger instanceof HTMLElement) trigger.click();

    setTimeout(() => {
      if (!this.#formOpen()) {
        if (trigger instanceof HTMLElement) trigger.style.removeProperty('display');
        holder?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }, 900);
  };
}

if (!customElements.get('s-review-write')) customElements.define('s-review-write', SReviewWrite);
